import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cake, CalendarBlank, Fire, Users, CheckCircle, Trash, Warning, Confetti, Share, WhatsappLogo, Copy, UserPlus, QrCode } from "@phosphor-icons/react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ListSkeleton } from "@/components/Skeleton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS_PT = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const WEEKDAYS_PT = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];

const ADMIN_WHATSAPP = "5521984317457";

const Birthday = () => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookedDates, setBookedDates] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [partyType, setPartyType] = useState("festa");
  const [timeStart, setTimeStart] = useState("10:00");
  const [guests, setGuests] = useState(10);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear] = useState(2026);
  const h = { headers: getAuthHeaders(), withCredentials: true };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [booked, mine] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/birthday/booked-dates`, h),
        axios.get(`${BACKEND_URL}/api/birthday/my-bookings`, h),
      ]);
      setBookedDates(booked.data);
      setMyBookings(mine.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleBook = async (e) => {
    e.preventDefault();
    if (!selectedDate) { toast.error("Selecione uma data no calendário"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/birthday/book`, {
        party_type: partyType, date: selectedDate, time_start: timeStart, guests, notes
      }, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true });
      toast.success(res.data.message);

      // Send booking confirmation + notes to admin via WhatsApp
      const typeLabel = partyType === "festa" ? "Festa de Aniversário" : "Churrasqueira";
      const dateFormatted = new Date(selectedDate + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
      let msg = `📋 *NOVA RESERVA - MARABU OPEN CLUB*\n\n🎉 *Tipo:* ${typeLabel}\n📅 *Data:* ${dateFormatted}\n🕐 *Horário:* ${timeStart}\n👥 *Convidados:* ${guests}\n👤 *Cliente:* ${user?.name || "N/A"}\n📧 *Email:* ${user?.email || "N/A"}`;
      if (notes.trim()) {
        msg += `\n\n📝 *Observações:*\n${notes}`;
      }
      msg += `\n\n✅ Reserva confirmada!`;
      window.open(`https://wa.me/${ADMIN_WHATSAPP}?text=${encodeURIComponent(msg)}`, "_blank");

      setSelectedDate("");
      setNotes("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao reservar");
    } finally { setSubmitting(false); }
  };

  const cancelBooking = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/birthday/${id}`, h);
      toast.success("Reserva cancelada");
      fetchData();
    } catch (err) { toast.error("Erro ao cancelar"); }
  };

  // Calendar logic
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const today = new Date().toISOString().split("T")[0];
  const bookedMap = {};
  bookedDates.forEach(b => { bookedMap[b.date] = b; });

  const userBirthday = user?.birth_date ? (() => {
    const bd = new Date(user.birth_date + "T12:00:00");
    return `2026-${String(bd.getMonth()+1).padStart(2,"0")}-${String(bd.getDate()).padStart(2,"0")}`;
  })() : null;

  if (loading) return <div><h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E] mb-6">Meu Aniversário</h2><ListSkeleton count={3} /></div>;

  return (
    <div data-testid="birthday-tab">
      <div className="flex items-center gap-3 mb-2">
        <Cake size={32} weight="duotone" className="text-[#FF5A26]" />
        <h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E]">Meu Aniversário</h2>
      </div>
      <p className="text-[#115E59] mb-8">Reserve a festa ou churrasqueira do MARABU para comemorar!</p>

      {/* Birthday info */}
      {user?.birth_date && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-[#FF5A26] to-[#E04818] rounded-2xl p-6 mb-8 text-white flex items-center gap-4"
          data-testid="birthday-info">
          <Confetti size={40} weight="fill" />
          <div>
            <p className="font-bold text-lg">Seu aniversário: {new Date(user.birth_date + "T12:00:00").toLocaleDateString("pt-BR", { day: "numeric", month: "long" })}</p>
            <p className="text-white/80 text-sm">Escolha uma data abaixo para reservar sua comemoração!</p>
          </div>
        </motion.div>
      )}

      {!user?.birth_date && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5 mb-8 flex items-start gap-3" data-testid="no-birthday-warning">
          <Warning size={24} weight="fill" className="text-yellow-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-[#042F2E]">Data de nascimento não informada</p>
            <p className="text-sm text-[#115E59]">Vá na aba <strong>Perfil</strong> e adicione sua data de nascimento para receber lembretes!</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calendar */}
        <div className="bg-white rounded-2xl border border-[#E4DFD3] p-6 shadow-sm" data-testid="birthday-calendar">
          <div className="flex items-center justify-between mb-5">
            <button onClick={() => setCalMonth(m => Math.max(0, m - 1))}
              className="w-10 h-10 rounded-full bg-[#F4F1EA] flex items-center justify-center font-bold text-[#042F2E] hover:bg-[#E4DFD3]"
              disabled={calMonth === 0}>
              &lt;
            </button>
            <h3 className="text-lg font-bold text-[#042F2E]">{MONTHS_PT[calMonth]} {calYear}</h3>
            <button onClick={() => setCalMonth(m => Math.min(11, m + 1))}
              className="w-10 h-10 rounded-full bg-[#F4F1EA] flex items-center justify-center font-bold text-[#042F2E] hover:bg-[#E4DFD3]"
              disabled={calMonth === 11}>
              &gt;
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS_PT.map(d => (
              <div key={d} className="text-center text-xs font-bold text-[#115E59] py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const dateInfo = bookedMap[dateStr];
              const isFull = dateInfo?.full;
              const isPast = dateStr < today;
              const isSelected = dateStr === selectedDate;
              const isBirthday = dateStr === userBirthday;
              const isToday = dateStr === today;

              return (
                <button
                  key={day}
                  onClick={() => {
                    if (!isPast && !isFull) setSelectedDate(dateStr);
                  }}
                  disabled={isPast || isFull}
                  className={`relative aspect-square rounded-xl text-sm font-semibold transition-all flex items-center justify-center
                    ${isSelected ? "bg-[#FF5A26] text-white ring-2 ring-[#FF5A26] ring-offset-2" : ""}
                    ${isFull ? "bg-red-100 text-red-400 cursor-not-allowed" : ""}
                    ${isPast && !isFull ? "text-[#E4DFD3] cursor-not-allowed" : ""}
                    ${!isPast && !isFull && !isSelected ? "hover:bg-[#FFF4E6] text-[#042F2E]" : ""}
                    ${isToday && !isSelected ? "ring-2 ring-[#0891B2] ring-inset" : ""}
                    ${isBirthday && !isSelected ? "bg-[#FFF4E6] ring-2 ring-[#FF5A26] ring-inset" : ""}
                  `}
                  data-testid={`cal-day-${dateStr}`}
                >
                  {day}
                  {isBirthday && <Cake size={10} weight="fill" className="absolute -top-0.5 -right-0.5 text-[#FF5A26]" />}
                  {dateInfo && !isFull && <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-yellow-500" />}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-4 mt-5 text-xs text-[#115E59]">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#FF5A26]" /> Selecionado</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-200" /> Lotado (3/3)</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-yellow-500" /> Parcial</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded ring-2 ring-[#0891B2]" /> Hoje</span>
            {userBirthday && <span className="flex items-center gap-1.5"><Cake size={12} weight="fill" className="text-[#FF5A26]" /> Seu aniversário</span>}
          </div>
        </div>

        {/* Booking form */}
        <div>
          <form onSubmit={handleBook} className="bg-white rounded-2xl border border-[#E4DFD3] p-6 shadow-sm space-y-5" data-testid="birthday-form">
            <h3 className="text-lg font-bold text-[#042F2E]">Reservar Comemoração</h3>

            {selectedDate ? (
              <div className="bg-[#FFF4E6] rounded-xl p-4 flex items-center gap-3">
                <CalendarBlank size={24} weight="fill" className="text-[#FF5A26]" />
                <div>
                  <p className="font-bold text-[#042F2E]">
                    {new Date(selectedDate + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                  </p>
                  <p className="text-xs text-[#115E59]">Data selecionada</p>
                </div>
              </div>
            ) : (
              <div className="bg-[#F4F1EA] rounded-xl p-4 text-center text-sm text-[#115E59]">
                Clique em uma data no calendário para selecionar
              </div>
            )}

            <div>
              <Label className="text-sm font-bold text-[#115E59]">Tipo de Reserva</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <button type="button" onClick={() => setPartyType("festa")}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${
                    partyType === "festa" ? "border-[#FF5A26] bg-[#FFF4E6]" : "border-[#E4DFD3] hover:border-[#FF5A26]"
                  }`} data-testid="type-festa">
                  <Confetti size={28} weight="duotone" className={`mx-auto mb-2 ${partyType === "festa" ? "text-[#FF5A26]" : "text-[#115E59]"}`} />
                  <p className="font-bold text-sm text-[#042F2E]">Festa</p>
                  <p className="text-xs text-[#115E59]">Salão + decoração</p>
                </button>
                <button type="button" onClick={() => setPartyType("churrasqueira")}
                  className={`p-4 rounded-xl border-2 text-center transition-all ${
                    partyType === "churrasqueira" ? "border-[#FF5A26] bg-[#FFF4E6]" : "border-[#E4DFD3] hover:border-[#FF5A26]"
                  }`} data-testid="type-churrasqueira">
                  <Fire size={28} weight="duotone" className={`mx-auto mb-2 ${partyType === "churrasqueira" ? "text-[#FF5A26]" : "text-[#115E59]"}`} />
                  <p className="font-bold text-sm text-[#042F2E]">Churrasqueira</p>
                  <p className="text-xs text-[#115E59]">Área de churrasco</p>
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-bold text-[#115E59]">Horário de Início</Label>
              <select value={timeStart} onChange={(e) => setTimeStart(e.target.value)}
                className="w-full rounded-xl border border-[#E4DFD3] px-4 py-3 text-sm mt-2 font-semibold text-[#042F2E]"
                data-testid="time-start-select">
                {["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00"].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-sm font-bold text-[#115E59]">Número de Convidados</Label>
              <div className="flex items-center gap-3 mt-2">
                <Users size={20} weight="duotone" className="text-[#115E59]" />
                <Input type="number" value={guests} onChange={(e) => setGuests(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1} max={100} className="rounded-xl border-[#E4DFD3] w-24" data-testid="guests-input" />
                <span className="text-sm text-[#115E59]">pessoas</span>
              </div>
            </div>

            <div>
              <Label className="text-sm font-bold text-[#115E59]">Observações (opcional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex: Preciso de mesa para bolo, som, etc..."
                className="rounded-xl border-[#E4DFD3] mt-2" data-testid="notes-input" />
            </div>

            <Button type="submit" disabled={submitting || !selectedDate}
              className="w-full bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full py-6 text-lg"
              data-testid="book-birthday-btn">
              {submitting ? "Reservando..." : "Confirmar Reserva"}
            </Button>
          </form>

          {/* My bookings with invite management */}
          {myBookings.length > 0 && (
            <div className="mt-6" data-testid="my-bookings">
              <h3 className="text-lg font-bold text-[#042F2E] mb-3">Minhas Reservas</h3>
              <div className="space-y-4">
                {myBookings.map((b) => (
                  <BookingCard key={b.id} booking={b} onCancel={cancelBooking} h={h} getAuthHeaders={getAuthHeaders} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Booking card with invite management
const BookingCard = ({ booking: b, onCancel, h, getAuthHeaders }) => {
  const [invites, setInvites] = useState([]);
  const [showInvites, setShowInvites] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);

  const loadInvites = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/birthday/${b.id}/invites`, h);
      setInviteInfo(res.data);
      setInvites(res.data.invites);
    } catch (e) { console.error(e); }
  };

  const generateInvite = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/birthday/${b.id}/invite`, {}, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" }, withCredentials: true,
      });
      toast.success("Convite gerado!");
      // Share via WhatsApp
      const typeLabel = b.party_type === "festa" ? "Festa de Aniversário" : "Churrasqueira";
      const dateStr = new Date(b.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "numeric", month: "long" });
      const msg = `🎉 Você está convidado(a)!\n\n*${typeLabel}*\n📅 ${dateStr}\n⏰ ${b.time_start || ""}\n📍 MARABU OPEN CLUB\n\nAceite o convite aqui:\n${res.data.invite_url}\n\n⚠️ Cadastre-se no app para garantir entrada grátis!`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
      loadInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar convite");
    } finally { setGenerating(false); }
  };

  const cancelInvite = async (code) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/invite/${code}`, h);
      toast.success("Convite cancelado");
      loadInvites();
    } catch (e) { toast.error("Erro"); }
  };

  useEffect(() => { if (showInvites) loadInvites(); }, [showInvites]);

  return (
    <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl border overflow-hidden ${b.status === "cancelled" ? "border-red-200 opacity-60" : "border-[#E4DFD3]"}`}
      data-testid={`booking-${b.id}`}>
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {b.party_type === "festa" ? <Confetti size={24} weight="duotone" className="text-[#FF5A26]" /> : <Fire size={24} weight="duotone" className="text-[#FF5A26]" />}
          <div>
            <p className="font-bold text-[#042F2E]">
              {b.party_type === "festa" ? "Festa" : "Churrasqueira"} — {new Date(b.date + "T12:00:00").toLocaleDateString("pt-BR")}
            </p>
            <p className="text-xs text-[#115E59]">{b.guests} convidados • {b.status === "cancelled" ? "Cancelada" : "Confirmada"}</p>
          </div>
        </div>
        {b.status !== "cancelled" && (
          <Button onClick={() => onCancel(b.id)} variant="ghost" className="text-red-500 hover:bg-red-50 rounded-full" data-testid={`cancel-${b.id}`}>
            <Trash size={18} />
          </Button>
        )}
      </div>

      {/* Invite actions */}
      {b.status === "confirmed" && (
        <div className="border-t border-[#E4DFD3] p-4 bg-[#F4F1EA]">
          <div className="flex gap-2">
            <Button onClick={generateInvite} disabled={generating}
              className="flex-1 bg-[#FF5A26] hover:bg-[#E04818] text-white font-bold rounded-full text-sm"
              data-testid={`gen-invite-${b.id}`}>
              <UserPlus size={16} weight="bold" className="mr-1.5" />
              {generating ? "Gerando..." : "Gerar Convite"}
            </Button>
            <Button onClick={() => setShowInvites(!showInvites)} variant="outline"
              className="rounded-full border-[#E4DFD3] text-sm font-bold"
              data-testid={`show-invites-${b.id}`}>
              <Users size={16} className="mr-1.5" />
              {showInvites ? "Fechar" : "Ver Convites"}
            </Button>
          </div>

          {/* Invite list */}
          <AnimatePresence>
            {showInvites && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                className="mt-4 overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-[#115E59] uppercase tracking-wider">
                    Convites ({invites.length}/{inviteInfo?.max || b.guests})
                  </p>
                </div>
                {invites.length === 0 ? (
                  <p className="text-sm text-[#115E59] py-2">Nenhum convite gerado ainda</p>
                ) : (
                  <div className="space-y-2">
                    {invites.map((inv) => (
                      <div key={inv.id} className="bg-white rounded-xl p-3 flex items-center justify-between border border-[#E4DFD3]"
                        data-testid={`invite-${inv.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                            inv.status === "accepted" ? "bg-[#10B981]" : inv.status === "cancelled" ? "bg-red-400" : "bg-[#0891B2]"
                          }`}>
                            {inv.status === "accepted" ? <CheckCircle size={16} weight="fill" className="text-white" /> :
                             inv.status === "cancelled" ? <Trash size={16} weight="fill" className="text-white" /> :
                             <UserPlus size={16} weight="bold" className="text-white" />}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#042F2E] truncate">
                              {inv.guest_name || "Aguardando..."}
                            </p>
                            <p className="text-[10px] text-[#115E59] font-mono">{inv.id}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {inv.status === "pending" && (
                            <>
                              <Button onClick={() => {
                                const url = `${window.location.origin}/convite/${inv.id}`;
                                navigator.clipboard.writeText(url);
                                toast.success("Link copiado!");
                              }} variant="ghost" className="rounded-full p-1.5 text-[#0891B2]" data-testid={`copy-invite-${inv.id}`}>
                                <Copy size={16} />
                              </Button>
                              <Button onClick={() => {
                                const url = `${window.location.origin}/convite/${inv.id}`;
                                const typeLabel = b.party_type === "festa" ? "Festa" : "Churrasqueira";
                                const msg = `🎉 Convite: ${typeLabel} no MARABU!\n${url}`;
                                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
                              }} variant="ghost" className="rounded-full p-1.5 text-[#25D366]">
                                <WhatsappLogo size={16} weight="fill" />
                              </Button>
                              <Button onClick={() => cancelInvite(inv.id)} variant="ghost" className="rounded-full p-1.5 text-red-400">
                                <Trash size={14} />
                              </Button>
                            </>
                          )}
                          {inv.status === "accepted" && (
                            <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">Aceito</span>
                          )}
                          {inv.status === "cancelled" && (
                            <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold">Cancelado</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default Birthday;
